/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import premium1x from "./images/premium@1x.png";
import premium2x from "./images/premium@2x.png";
import podcastIcon from "./images/podcast-icon.svg";
import * as React from "react";
import { devices } from "components/shared/Styles";
import { useCurrentUser } from "actions/CurrentUserActions";

export const PlansContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 48px;
  justify-content: space-between;
  margin: 20px 0 48px 0;

  ${devices.small} {
    flex-direction: column-reverse;
    gap: 24px;
  }
`;

export const Plan = styled.div<{
  premium?: boolean;
}>`
  border-radius: 32px;
  color: ${(props): string => (props.premium ? "white" : "black")};
  background-color: ${(props): string =>
    props.premium
      ? "var(--spectrum-global-color-static-indigo-600)"
      : "var(--spectrum-gray-100)"};
  padding: 32px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-between;
  max-width: 100%;
  font-size: 18px;
  font-weight: 300;
  line-height: 20px;
`;

export const PlanTitleContainer = styled.h2`
  height: 48px;
  font-size: 22px;
  font-weight: 900;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const PlanSubtitleContainer = styled.p`
  font-weight: 400;
`;

export const PlanTitleText = styled.span`
  flex: 2;
`;

export const PlanTitle: React.FC = () => {
  const { currentUser } = useCurrentUser();
  // Already entitled users may have gotten access via Firefly Pro, Adobe
  // Express, or Podcast Premium, so we can't say which product to name.
  const planTitleText = currentUser?.hasPremiumAccess
    ? I18n.t("plans.premium.title")
    : I18n.t("plans.premium.title_firefly_pro");
  return (
    <PlanTitleContainer>
      <PlanTitleText>{planTitleText}</PlanTitleText>
      <PremiumLogo />
    </PlanTitleContainer>
  );
};

export const Products = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;
  flex-grow: 2;
`;

export const Product = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 300;
`;

export const ProductTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;

  ${devices.small} {
    font-size: 16px;
  }
`;

export const CallToAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

export const PodcastLogo = styled.img.attrs({
  src: podcastIcon,
  width: "56px",
  height: "54px",
  "aria-hidden": "true",
})``;

export const PremiumLogo = styled.img.attrs({
  srcSet: `${premium1x} 1x, ${premium2x} 2x`,
  "aria-hidden": "true",
})``;
